import { NATURE_TEXT } from './emergency';
import { NEEDS } from './patience';
import type { DebriefEvent, Sim, SimEvent } from './sim-contracts';

const REPORTED_EVENTS = new Set<SimEvent['type']>([
  'conflictStart', 'goAround', 'missedIntercept', 'exited', 'unable',
  'abortedTakeoff', 'pilotWaiting', 'emergencyDeclared', 'fuelEmergency', 'forcedLanding',
]);

/** Build the bounded teaching timeline independently of the simulator clock and UI. */
export function attachDebriefTimeline(sim: Sim, timeline: DebriefEvent[]): () => void {
  const append = (event: DebriefEvent) => {
    timeline.push(event);
    if (timeline.length > 100) timeline.shift();
  };

  return sim.on('event', (event) => {
    if (!REPORTED_EVENTS.has(event.type)) return;
    if (event.type === 'emergencyDeclared' || event.type === 'fuelEmergency' || event.type === 'forcedLanding') {
      const detail = event.type === 'forcedLanding'
        ? 'The aircraft ran out of fuel. A crew that reports minimum fuel can accept no further delay: give it the shortest track to the runway.'
        : event.type === 'fuelEmergency'
          ? 'A fuel emergency is a distress call. Clear the approach, keep the aircraft on the shortest routing and hold other traffic off.'
          : `${NATURE_TEXT[event.nature]}: give the flight priority, ask what it needs, and keep the frequency clear for it.`;
      append({ timeS: sim.simTimeS, type: event.type, callsigns: [event.ac.callsign], detail });
      return;
    }
    if (event.type === 'pilotWaiting') {
      if (event.stage < 2) return;
      const waited = `${Math.floor(event.waitedS / 60)}:${String(Math.floor(event.waitedS % 60)).padStart(2, '0')}`;
      const action = event.action === 'returnToStand'
        ? ' and returned to the stand'
        : event.action === 'hold'
          ? (event.fix ? ` and entered a hold at ${event.fix}` : ' and stopped following the arrival')
          : '';
      append({
        timeS: sim.simTimeS,
        type: event.type,
        callsigns: [event.ac.callsign],
        detail: `Waited ${waited} for ${NEEDS[event.need].text}${action}. Answer every call, even with “stand by”, and give the pilot what they asked for.`,
      });
      return;
    }
    const callsigns = 'a' in event ? [event.a.callsign, event.b.callsign] : 'ac' in event ? [event.ac.callsign] : [];
    const detail = 'reason' in event
      ? event.reason
      : event.type === 'conflictStart'
        ? 'Separation lost. Establish spacing before assigning another approach.'
        : event.type === 'missedIntercept'
          ? 'The aircraft missed the localizer. Set an intercept heading before clearing the approach.'
          : event.type === 'exited'
            ? 'The aircraft left the sector. Check its requested exit and cleared altitude.'
            : 'A safety action stopped the takeoff.';
    append({ timeS: sim.simTimeS, type: event.type, callsigns, detail });
  });
}
